// Drive a one-shot REST resource LIVE off the event bus instead of polling.
//
// The pattern: a view does ONE initial `useResource` fetch on mount to seed, then
// this hook reconciles that seed against the live store — which the event-bus
// transport keeps current from the FROZEN {type,ts,id,data} frames. It calls
// `reload(true)` (a QUIET refresh — no loading flash) when:
//
//   • `signal(state)` changes — derive it from the store slices the bus updates
//     for THIS resource (e.g. publications + client statuses), so a relevant event
//     triggers exactly one reconcile fetch — NOT a setInterval tick; and
//   • the bus (re)connects — a false→true edge — so the seed can't drift across a
//     dropped/restored connection.
//
// `fallbackMs` adds a SINGLE low-frequency reconcile for the parts of a resource
// the bus emits NO event for (e.g. service-definition edits, provider-instance
// runtime state) — replacing the old tight poll, not reinstating it.

import { useEffect, useRef } from 'react';
import { useStore, type DashboardState } from '../store/store';

export function useLiveReseed(
  reload: (quiet?: boolean) => void,
  signal: (s: DashboardState) => string | number,
  opts: { fallbackMs?: number } = {},
): void {
  const sig = useStore(signal);
  const connected = useStore((s) => s.connected);
  const firstSig = useRef(true);
  const prevConnected = useRef(connected);
  const { fallbackMs } = opts;

  // Event-driven reconcile: a store slice this resource cares about changed. Skip
  // the first render (that's the initial seed already in flight).
  useEffect(() => {
    if (firstSig.current) {
      firstSig.current = false;
      return;
    }
    reload(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  // Reconnect reseed: only on a false→true edge (not on the initial value).
  useEffect(() => {
    if (connected && !prevConnected.current) reload(true);
    prevConnected.current = connected;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  // Low-frequency reconcile fallback for resources without bus events.
  useEffect(() => {
    if (!fallbackMs) return;
    const t = setInterval(() => reload(true), fallbackMs);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallbackMs]);
}

// --- small signal builders (stable, value-equal strings) -------------------

/** Signature of the publication index: id:status pairs. Changes on publication.changed. */
export function publicationsSig(s: DashboardState): string {
  return Object.values(s.publications)
    .map((p) => `${p.id}:${p.status}`)
    .sort()
    .join(',');
}

/** Signature of the client index: id:status pairs. Changes on client.status (online/
 * offline + a newly-connecting node), but NOT on the frequent client.metrics heartbeat. */
export function clientsSig(s: DashboardState): string {
  return Object.values(s.clients)
    .map((c) => `${c.id}:${c.status}`)
    .sort()
    .join(',');
}

/** Signature of route instances: instance_id:status pairs. Changes on route.instantiated/
 * route.completed/route.errored. */
export function routeInstancesSig(s: DashboardState): string {
  return Object.values(s.routeInstances)
    .map((r) => `${r.instance_id}:${r.status}`)
    .sort()
    .join(',');
}

/** Signature of the live task projection (id:state). Changes on ``task.updated``
 * so the Tasks list re-fetches in realtime. */
export function tasksSig(s: DashboardState): string {
  return Object.values(s.tasks)
    .map((t) => `${t.id}:${t.state}`)
    .sort()
    .join(',');
}

/** Signature of the live call log (count + newest id). Changes on ``call.logged``
 * so the Calls list re-fetches in realtime. */
export function callsSig(s: DashboardState): string {
  return `${s.calls.length}:${s.calls[0]?.id ?? ''}`;
}
